import { css } from '@emotion/react';
import type { EmotionJSX } from '@emotion/react/types/jsx-namespace';
import { Box, desktopFirstMediaQuery } from '@inflearn/ds-react';
import { isNil } from 'lodash-es';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Sidebar } from '../../../FSD/shared/ui/sidebar/Sidebar';
import { GiftCourseWelcomeModal } from '../../common/GiftCourseWelcomeModal';

import AppShellLayoutForCSR from '../AppShellLayoutForCSR';
import CustomHead, { type CustomHeadProps } from '../CustomHead';
import { DefaultGuard } from '../DefaultLayout';

interface MyPageLayoutProps {
  children: ReactNode;
  meta?: CustomHeadProps & {
    title: string;
    description?: string;
    pageCategory?: string;
  };
  requiredLogin?: boolean;
  hideLNB?: boolean;
  guard?: ({ children }: { children: ReactNode }) => EmotionJSX.Element;
  showChanneltalk?: boolean;
  showLanguageSectionButton?: boolean;
  useUnauthorizedHeader?: boolean;
}

const MyPageLayout = ({
  guard: Guard = DefaultGuard,
  children,
  meta,
  requiredLogin = true,
  hideLNB = false,
  showChanneltalk = true,
  showLanguageSectionButton = true,
  useUnauthorizedHeader = false,
}: MyPageLayoutProps) => {
  const { t } = useTranslation();

  return (
    <Guard>
      {!isNil(meta) && (
        <CustomHead
          {...meta}
          title={meta.title}
          siteTitle={t('인프런')}
          description={meta.description}
          pageCategory={meta.pageCategory ?? t('메타.마이페이지.페이지카테고리')}
        />
      )}
      <AppShellLayoutForCSR
        requiredLogin={requiredLogin}
        showChanneltalk={showChanneltalk}
        showLanguageSectionButton={showLanguageSectionButton}
        useUnauthorizedHeader={useUnauthorizedHeader}
      >
        <Box component="main" mih="100vh">
          <div
            css={css([
              {
                display: 'flex',
                flex: '1 1 0',
                padding: '32px',
                margin: '0 auto',
                maxWidth: '1200px',
                gap: '56px',

                [desktopFirstMediaQuery.mobile]: {
                  padding: '16px',
                },
              },
              hideLNB ? { justifyContent: 'center' } : undefined,
            ])}
          >
            {hideLNB ? null : (
              <aside css={styleLNBWrapper}>
                <Sidebar />
              </aside>
            )}
            <div css={styleContentWrapper}>{children}</div>
          </div>
        </Box>
      </AppShellLayoutForCSR>
      <GiftCourseWelcomeModal />
    </Guard>
  );
};

const ONE_OF_COLUMN = 100 / 12;

const styleLNBWrapper = css({
  minWidth: 174,

  [desktopFirstMediaQuery.tablet]: {
    display: 'none',
  },
});

const styleContentWrapper = css({
  width: `${ONE_OF_COLUMN * 10}%`,
  minWidth: 0,

  [desktopFirstMediaQuery.tablet]: {
    width: '100%',
    marginLeft: 0,
  },
});

export default MyPageLayout;
